import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse } from 'src/app/models/api-response.model';
import { ChartDraft, UpsertChartDraftRequest } from 'src/app/models/chart-draft.model';
import { environment } from 'src/environments/environment';

/**
 * Data layer for the temporary chart-draft storage.
 *
 * Persists one draft per (clinician, patient) via the core API. The backend derives the
 * clinician from the auth token and stores the document at
 * `users/{clinicianId}/chart_drafts/{patientId}`, so the client only ever addresses a patient.
 *
 * Follows the project convention: type the call as `ApiResponse<T>` and unwrap `.data` here in
 * the service (never in components). See `AcuityService` for the reference implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class ChartDraftService {
  private baseUrl = `${environment.welbyEndpoint}/api/v1/core/patients`;

  constructor(private http: HttpClient) {}

  /** Returns the current draft for the patient, or null when none exists. */
  getDraft(patientId: string): Observable<ChartDraft | null> {
    return this.http
      .get<ApiResponse<ChartDraft | null>>(`${this.baseUrl}/${patientId}/chart-draft`)
      .pipe(map((res) => res.data ?? null));
  }

  /** Creates or replaces the patient's draft (upsert — only the latest is kept). */
  upsertDraft(patientId: string, dto: UpsertChartDraftRequest): Observable<ChartDraft> {
    return this.http
      .put<ApiResponse<ChartDraft>>(`${this.baseUrl}/${patientId}/chart-draft`, dto)
      .pipe(map((res) => res.data));
  }

  /** Removes the patient's draft entirely (on discard, time save/discard, or ignoring the modal). */
  deleteDraft(patientId: string): Observable<void> {
    return this.http
      .delete<ApiResponse<void>>(`${this.baseUrl}/${patientId}/chart-draft`)
      .pipe(map(() => undefined));
  }

  /**
   * Clears only the in-progress edit (keeping preserved time). The backend clears it only when the
   * stored edit matches `type`, so saving one surface never drops a draft belonging to another.
   */
  clearDraftEdit(patientId: string, type: string): Observable<void> {
    return this.http
      .delete<ApiResponse<void>>(`${this.baseUrl}/${patientId}/chart-draft/edit`, { params: { type } })
      .pipe(map(() => undefined));
  }
}
